/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { DownloadType, ScopeTotals } from '../../types/download';
import { useRouteMatch } from 'react-router-dom';
import { BasicAlert } from '../alert/BasicAlert';
import { DownloadSettingsType, getDownLoadOptions } from './util/downloadReportHandler';
import { DownloadQuestionSettings } from './DownloadQuestionSettings';
import { DownloadSettingOptions } from './DownloadSettingOptions';
import { ScopeSelect } from './ScopeSelect';
import { useAppSelector } from '../../reducers';
import { isStaff } from '../../selectors/user';

export const generatingReportText = 'Generating report...';

export interface OptionTitle {
  title?: string,
  tooltip?: string
}
export interface DownloadSettingPropType {
  isOpen: boolean;
  isDisabled?: boolean;
  title: string;
  downloadDoc: () => Promise<void> | undefined;
  onDownload: (type: DownloadType, config?: { packCode?: string; debug?: boolean }) => Promise<void>;
  handleClose: () => void;
  handleChange?: React.ChangeEventHandler<HTMLInputElement>;
  downloadSettings?: DownloadSettingsType;
  code?: string;
  requiredScope?: string;
  titleOptions?: {
    word?: OptionTitle;
    simple?: OptionTitle;
    detailed?: OptionTitle;
    scopes?: OptionTitle;
  };
  totals?: ScopeTotals;
  disabledOptions?: string[];
}

export const DownloadSettingModal = (props: DownloadSettingPropType) => {
  const {
    isOpen,
    isDisabled = false,
    title,
    downloadSettings,
    handleClose,
    handleChange,
    onDownload,
    downloadDoc,
    code,
    requiredScope,
    totals,
    disabledOptions = [],
    titleOptions,
  } = props;

  const [isGeneratingReport, setGeneratingReport] = useState(false);
  const userIsStaff = useAppSelector(isStaff);

  const match = useRouteMatch<{ portfolioId?: string }>();
  const isPortfolioTracker = !!match.params.portfolioId;

  const handleDownloadReport = async (downloadType: DownloadType, config?: { packCode?: string, debug?: boolean }) => {
    setGeneratingReport(true);
    await onDownload(downloadType, config);
    handleClose();
    setGeneratingReport(false);
  }

  const handleDownloadDoc = async () => {
    setGeneratingReport(true);
    await downloadDoc();
    handleClose();
    setGeneratingReport(false);
  }

  const options = getDownLoadOptions({
    code,
    titleOptions,
    disabledOptions,
    handleDownloadReport,
    handleDownloadDoc,
    isStaff: userIsStaff
  });

  const selectedGroups = downloadSettings?.selectedScopes ?? [];

  // We have scopes, but nothing is selected
  const isEmptyScope = selectedGroups.length > 0 && selectedGroups.every(item => !item.checked);

  return (
    <Modal className='download-setting__modal' isOpen={isOpen} toggle={handleClose} backdrop='static'>
      <ModalHeader toggle={handleClose}>{title}</ModalHeader>
      <ModalBody>
        {isGeneratingReport ? (
          <BasicAlert type='info'>{generatingReportText}</BasicAlert>
        ) : (
          <>
            <DownloadQuestionSettings
              code={code}
              isDisabled={isEmptyScope}
              totals={totals}
              isPortfolioTracker={isPortfolioTracker}
              downloadSettings={downloadSettings}
              handleChange={handleChange}
            />
            <ScopeSelect
              hidden={!downloadSettings || code === 'sdg'}
              requiredScope={requiredScope}
              isEmptyScope={isEmptyScope}
              selectedGroups={selectedGroups}
              titleOptions={titleOptions}
              handleChange={handleChange}
            />

            <DownloadSettingOptions
              options={options}
              isDisabled={isDisabled || isEmptyScope}
            />
          </>
        )}
      </ModalBody>
      <ModalFooter className='text-right'>
        <Button outline size='sm' className='mr-2 pl-4 pr-4' onClick={handleClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
